import React, { FC } from 'react'
import { Collapse, Flex, Typography } from 'antd'
import { getClassNames } from '../../_utils/classnames'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'

/**
 * Inline guidance on how wording changes which tier a rule lands in (NS-2648).
 *
 * EXAMPLES ARE DRAWN FROM THE REAL SPECIMEN CORPUS — the publisher and media brand voices this
 * platform is actually used for (People, MarketWatch, Sports Illustrated, CNN.com, live events).
 * That matters because the two things a news desk writes most are a headline length cap and a
 * banned-hype-word list, and the two things they most often expect to be enforceable but cannot
 * be are "always name the team" and "emoji are fine except on breaking news". An earlier draft
 * used examples shaped by a test directive instead, and taught the wrong lessons well.
 *
 * TWO LEVERS, TWO LIMITS, in that order. The levers are the only phrasings measurement found that
 * convert a rule to Guaranteed. The limits exist because advice that only ever offers a rewrite
 * implies one always exists:
 *
 *   - A CHANGING SUBJECT cannot be a fixed rule. "Always name the celebrity/team/artist" appears
 *     in 3 of the 5 specimens, and the ground truth flags it as a trap: `required_terms` holds
 *     literals, and the entity is different every send. Authors reasonably expect this one to be
 *     deterministic, which is exactly why it needs saying.
 *   - EXCEPTIONS cannot be guaranteed at all. 7% of real mandates are conditional, and
 *     `CONTENT_DIRECTIVE_PROFILE` is depth-1 and AND-only, so the qualifier has nowhere to live.
 *
 * Voice and tone — 20% of everything authors state, and what a brand voice is FOR — is handled in
 * the intro rather than as a tip. Putting it first reframes the panel from a list of corrections
 * into an orientation, and it is reassurance, not instruction.
 *
 * Deliberately advice, not a form. A structured editor was measured against this same corpus and
 * would have captured 2 mandates out of 46 that free text does not already yield.
 */
interface IBrandVoiceAuthoringHelp {
    /** Open on arrival for an empty directive — a blank page is exactly when this is worth
     * reading, and exactly when it costs the author nothing to have it expanded. */
    defaultOpen?: boolean
}

interface Tip {
    tier: keyof typeof BRAND_VOICE_TIER_VISUAL
    title: string
    /** The rewrite, when there is one: what the author typed, and what to type instead. */
    from?: string
    to?: string
    /** Used instead of from/to when no rewrite exists: the phrasing, and why it stays judged. */
    example?: string
    note?: string
}

const Example: FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography.Text code className={getClassNames('bvh-example')}>
        {children}
    </Typography.Text>
)

/**
 * Two shapes on purpose. `from`/`to` tips have an actionable rewrite; `note` tips exist to say
 * that no rewrite will help, which is the whole reason they are here — advice that only ever
 * offers a rewrite implies one always exists.
 */
const TIPS: Tip[] = [
    {
        tier: 'guaranteed',
        title: 'Name a number',
        from: 'Keep headlines short',
        to: 'Headlines under 55 characters',
    },
    {
        tier: 'guaranteed',
        title: 'Quote the exact words',
        from: 'No clickbait',
        to: 'Never use “you won’t believe”',
    },
    {
        tier: 'checked',
        title: 'A changing subject can’t be a fixed rule',
        example: 'Always name the team or player',
        note: 'The subject is different every send, so an AI judge checks it rather than a fixed rule.',
    },
    {
        tier: 'checked',
        title: 'Exceptions are judged, not guaranteed',
        example: 'Emoji on lighter stories, never on breaking news',
        note: 'A rule that applies only sometimes can’t be guaranteed. Write it the way you mean it.',
    },
]

export const BrandVoiceAuthoringHelp: FC<IBrandVoiceAuthoringHelp> = ({ defaultOpen }) => (
    <Collapse
        ghost
        size="small"
        className={getClassNames('bvh-panel')}
        defaultActiveKey={defaultOpen ? ['how'] : undefined}
        items={[
            {
                key: 'how',
                label: (
                    <Typography.Text type="secondary" className={getClassNames('bvh-trigger')}>
                        What makes a rule guaranteed?
                    </Typography.Text>
                ),
                children: (
                    <Flex vertical gap={8}>
                        {/* Stated first, because the rest of the panel is about tiers and reads
                            as a list of things the author is doing wrong without it. */}
                        <Typography.Text type="secondary" className={getClassNames('bvh-intro')}>
                            Everything you write is used, and your voice and tone stay exactly as you wrote them. This
                            is about which rules we can check ourselves and which need an AI judge.
                        </Typography.Text>
                        {TIPS.map((tip, i) => (
                            <Flex key={i} align="flex-start" gap={8}>
                                {/* Same icon and colour as the tier's own section and chip, so the
                                    advice and its outcome share one visual language rather than
                                    asking the author to hold a second mapping in their head. */}
                                <span
                                    className={getClassNames('bvh-icon')}
                                    style={{ color: BRAND_VOICE_TIER_VISUAL[tip.tier].color }}
                                >
                                    {BRAND_VOICE_TIER_VISUAL[tip.tier].icon}
                                </span>
                                <div className={getClassNames('bvh-tip')}>
                                    <Typography.Text strong className={getClassNames('bvh-tip')}>
                                        {tip.title}
                                    </Typography.Text>
                                    {/* The rewrite gets its own line rather than sitting inside a
                                        sentence. In a 33%-width column an inline example wrapped
                                        mid-phrase, and a chip broken across two lines reads as two
                                        separate chips. */}
                                    <div className={getClassNames('bvh-detail')}>
                                        {tip.example ? (
                                            <Example>{tip.example}</Example>
                                        ) : (
                                            <>
                                                <Example>{tip.from}</Example>
                                                <span className={getClassNames('bvh-arrow')}>→</span>
                                                <Example>{tip.to}</Example>
                                            </>
                                        )}
                                    </div>
                                    {/* Its own block rather than trailing the chip inline. Sharing
                                        the flex row let it wrap onto the next line behind a
                                        leading em dash, which read as an orphaned punctuation
                                        mark starting a line. */}
                                    {tip.note && (
                                        <Typography.Text type="secondary" className={getClassNames('bvh-note')}>
                                            {tip.note}
                                        </Typography.Text>
                                    )}
                                </div>
                            </Flex>
                        ))}
                    </Flex>
                ),
            },
        ]}
    />
)
